/**
 * Pricing Page
 * Displays subscription tiers and pricing information
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import './LegalPages.css';

const PricingPage = () => {
  const { openCheckout, prices, isPro, gatingEnabled } = useSubscription();

  const monthlyPrice = prices.find(p => p.interval === 'monthly');
  const yearlyPrice = prices.find(p => p.interval === 'yearly');

  // Real amounts (USD). Prefer what the backend returns; fall back to the
  // figures the checkout/UpgradeModal uses so a missing/empty price payload
  // never renders NaN on the page.
  const monthlyAmount = Number(monthlyPrice?.price) || 12.99;
  const yearlyAmount = Number(yearlyPrice?.price) || 119.88;

  // Savings = how much the yearly billing saves vs. paying monthly all year.
  // Guard the maths so it can never evaluate to NaN (e.g. missing amounts).
  let yearlySavingsPct = null;
  if (Number.isFinite(monthlyAmount) && Number.isFinite(yearlyAmount) && monthlyAmount > 0) {
    const annualMonthlyCost = monthlyAmount * 12;
    const raw = (1 - yearlyAmount / annualMonthlyCost) * 100;
    if (Number.isFinite(raw)) yearlySavingsPct = Math.round(raw);
  }

  const handleSubscribe = (interval) => {
    const price = interval === 'monthly' ? monthlyPrice : yearlyPrice;
    if (price?.id) {
      openCheckout(price.id);
    }
  };

  return (
    <div className="legal-page pricing-page">
      <div className="legal-container">
        <header className="legal-header">
          <Link to="/" className="back-link">← Back to App</Link>
          <h1>Pricing</h1>
          <p className="tagline">Train smarter with AI-powered fitness guidance</p>
        </header>

        <section className="pricing-intro">
          <p>
            <strong>Kata.fit</strong> is your personal AI fitness companion. Track your workouts, 
            meals, and metrics with our free tier, or unlock the full power of AI features with Pro.
          </p>
        </section>

        <div className="pricing-tiers">
          {/* Free Tier */}
          <div className="pricing-card free">
            <div className="tier-header">
              <h2>Free</h2>
              <div className="price">
                <span className="amount">$0</span>
                <span className="period">forever</span>
              </div>
            </div>
            <p className="tier-description">
              Everything you need to track your fitness journey
            </p>
            <ul className="feature-list">
              <li>✓ Unlimited workout tracking</li>
              <li>✓ Meal and nutrition logging</li>
              <li>✓ Body metrics tracking (weight, measurements)</li>
              <li>✓ Progress photos and media</li>
              <li>✓ Join training dojos</li>
              <li>✓ Connect with friends</li>
              <li>✓ Basic statistics and history</li>
              <li>✓ Workout templates</li>
            </ul>
            <div className="tier-action">
              <Link to="/" className="btn btn-secondary">Get Started Free</Link>
            </div>
          </div>

          {/* Pro Tier */}
          <div className="pricing-card pro featured">
            <div className="featured-badge">Most Popular</div>
            <div className="tier-header">
              <h2>Pro</h2>
              <div className="price">
                <span className="amount">${monthlyAmount}</span>
                <span className="period">/month</span>
              </div>
              {yearlyPrice && (
                <div className="yearly-option">
                  or ${yearlyAmount}/year
                  {yearlySavingsPct != null && ` (save ${yearlySavingsPct}%)`}
                </div>
              )}
            </div>
            <p className="tier-description">
              Unlock the full power of AI-driven fitness guidance
            </p>
            <ul className="feature-list">
              <li>✓ Everything in Free, plus:</li>
              <li className="highlight">🤖 AI Training Assistant</li>
              <li>• Personalized daily training insights</li>
              <li>• Smart workout recommendations</li>
              <li>• Real-time exercise guidance during workouts</li>
              <li className="highlight">📸 AI Form Analysis</li>
              <li>• Pose detection on workout videos</li>
              <li>• Form feedback and corrections</li>
              <li>• Movement pattern analysis</li>
              <li className="highlight">🍽️ AI Nutrition Planning</li>
              <li>• Auto-generated meal suggestions</li>
              <li>• Nutrition optimization tips</li>
              <li className="highlight">🥋 Create Your Own Dojo</li>
              <li>• Lead and manage training groups</li>
              <li>• Set dojo requirements and goals</li>
            </ul>
            <div className="tier-action">
              {gatingEnabled ? (
                isPro() ? (
                  <button className="btn btn-primary" disabled>Current Plan</button>
                ) : (
                  <>
                    <button 
                      className="btn btn-primary" 
                      onClick={() => handleSubscribe('monthly')}
                    >
                      Subscribe Monthly
                    </button>
                    {yearlyPrice && (
                      <button 
                        className="btn btn-secondary" 
                        onClick={() => handleSubscribe('yearly')}
                      >
                        Subscribe Yearly{yearlySavingsPct != null && ` (Save ${yearlySavingsPct}%)`}
                      </button>
                    )}
                  </>
                )
              ) : (
                <div className="coming-soon">
                  <span>🚀 Pro features coming soon!</span>
                  <p>Currently all features are available for free during our beta period.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <section className="pricing-faq">
          <h2>Frequently Asked Questions</h2>
          
          <div className="faq-item">
            <h3>Can I cancel anytime?</h3>
            <p>
              Yes! You can cancel your Pro subscription at any time. You'll continue to have 
              access to Pro features until the end of your billing period.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>What happens to my data if I cancel?</h3>
            <p>
              Your workout history, meals, and all logged data remain yours forever. You'll 
              still be able to access everything with the Free tier. AI recommendations 
              generated while you were Pro will still be viewable.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>How does the AI assistant work?</h3>
            <p>
              Our AI analyzes your training history, progress, and goals to provide 
              personalized guidance. It learns from your patterns and adapts its 
              recommendations as you improve.
            </p>
          </div>
          
          <div className="faq-item">
            <h3>Is there a free trial?</h3>
            <p>
              The Free tier lets you explore the app fully. When you're ready for AI 
              features, upgrade to Pro. We may offer trials periodically—check back for 
              promotions.
            </p>
          </div>
        </section>

        <footer className="legal-footer">
          <div className="footer-links">
            <Link to="/terms-and-conditions">Terms & Conditions</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/refund">Refund Policy</Link>
            <Link to="/support">Support</Link>
          </div>
          <p className="copyright">© {new Date().getFullYear()} Kata.fit. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default PricingPage;
